/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable, inject } from '@angular/core';

import {
  DragArea,
  MovableCardsContainerGroupTypeEnum,
  MovableCardsContainerSingleColumnGroup,
  MovableCardsContainerTwoColumnsGroup,
  SimpleDomRect,
} from '../movable-cards-container.view-model';
import { BoundingRectMapService } from './bounding-rect-map.service';
import { RearrangeService } from './rearrange.service';
import { StateService } from './state.service';

@Injectable()
export class DragAreaService<T extends string = string> {
  readonly #boundingRectMapService = inject<BoundingRectMapService<T>>(BoundingRectMapService);
  readonly #rearrangeService = inject<RearrangeService<T>>(RearrangeService);
  readonly #state = inject<StateService<T>>(StateService);

  public buildDragAreas(
    draggedCardKey: string,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[]
  ): DragArea<T>[] {
    const { rectMap } = this.#boundingRectMapService.buildBoundingRectMapFromGroups(groups);

    let draggedCardIdx!: number;
    let draggedCardColumnIdx!: number;
    let draggedGroupType!: MovableCardsContainerGroupTypeEnum;
    const draggedGroupIdx = groups.findIndex((group) => {
      if (group.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
        const idx = group.cards.findIndex((card) => card.key === draggedCardKey);
        if (idx !== -1) {
          draggedCardIdx = idx;
          draggedGroupType = group.type;
          return true;
        }
      } else {
        const leftIdx = group.leftCards.findIndex((card) => card.key === draggedCardKey);
        if (leftIdx !== -1) {
          draggedCardIdx = leftIdx;
          draggedCardColumnIdx = 0;
          draggedGroupType = group.type;
          return true;
        }

        const rightIdx = group.rightCards.findIndex((card) => card.key === draggedCardKey);
        if (rightIdx !== -1) {
          draggedCardIdx = rightIdx;
          draggedCardColumnIdx = 1;
          draggedGroupType = group.type;
          return true;
        }
      }

      return false;
    });

    return this.buildGroupDragAreas(
      rectMap,
      groups,
      draggedCardKey,
      draggedCardIdx,
      draggedCardColumnIdx,
      draggedGroupType,
      draggedGroupIdx
    );
  }

  private buildGroupDragAreas(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardKey: string,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupType: MovableCardsContainerGroupTypeEnum,
    draggedGroupIdx: number
  ): DragArea<T>[] {
    const dragAreas: DragArea<T>[] = [];

    groups.forEach((group, groupIdx) => {
      if (group.type === MovableCardsContainerGroupTypeEnum.TwoColumns) {
        group.leftCards.forEach((card, cardIdx) => {
          const topDragArea = this.generateTopDragAreaForTwoColumnsGroupCard(virtualRects, card.key, cardIdx, groupIdx);
          topDragArea.swapFn = () => {
            return this.#rearrangeService.insertTop(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              0,
              groupIdx
            );
          };

          const bottomDragArea = this.generateBottomDragAreaForTwoColumnsGroupCard(virtualRects, groups, card.key, cardIdx, groupIdx);
          bottomDragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              0,
              groupIdx
            );
          };
          dragAreas.push(topDragArea, bottomDragArea);
        });

        group.rightCards.forEach((card, cardIdx) => {
          const topDragArea = this.generateTopDragAreaForTwoColumnsGroupCard(virtualRects, card.key, cardIdx, groupIdx);
          topDragArea.swapFn = () => {
            return this.#rearrangeService.insertTop(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              1,
              groupIdx
            );
          };

          const bottomDragArea = this.generateBottomDragAreaForTwoColumnsGroupCard(virtualRects, groups, card.key, cardIdx, groupIdx);
          bottomDragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              1,
              groupIdx
            );
          };

          dragAreas.push(topDragArea, bottomDragArea);
        });

        const leftColumnHeight = this.getLeftColumnHeight(group, virtualRects);
        const rightColumnHeight = this.getRightColumnHeight(group, virtualRects);

        if (rightColumnHeight > leftColumnHeight) {
          const dragArea = this.generateExtraSpaceDragAreaForTwoColumnsGroupLeftColumn(virtualRects, groups, groupIdx);
          dragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              null,
              null,
              0,
              groupIdx
            );
          };
          dragAreas.push(dragArea);
        } else if (leftColumnHeight > rightColumnHeight) {
          const dragArea = this.generateExtraSpaceDragAreaForTwoColumnsGroupRightColumn(virtualRects, groups, groupIdx);
          dragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              null,
              null,
              1,
              groupIdx
            );
          };
          dragAreas.push(dragArea);
        }
      } else {
        group.cards.forEach((card, cardIdx) => {
          const topLeftDragArea = this.generateTopLeftDragAreaForSingleColumnGroupCard(virtualRects, card.key, cardIdx, groupIdx);
          topLeftDragArea.swapFn = () => {
            return this.#rearrangeService.insertTop(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              0,
              groupIdx
            );
          };

          const topRightDragArea = this.generateTopRightDragAreaForSingleColumnGroupCard(virtualRects, card.key, cardIdx, groupIdx);
          topRightDragArea.swapFn = () => {
            return this.#rearrangeService.insertTop(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              1,
              groupIdx
            );
          };

          const bottomLeftDragArea = this.generateBottomLeftDragAreaForSingleColumnGroupCard(
            virtualRects,
            groups,
            card.key,
            cardIdx,
            groupIdx
          );
          bottomLeftDragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              0,
              groupIdx
            );
          };

          const bottomRightDragArea = this.generateBottomRightDragAreaForSingleColumnGroupCard(
            virtualRects,
            groups,
            card.key,
            cardIdx,
            groupIdx
          );
          bottomRightDragArea.swapFn = () => {
            return this.#rearrangeService.insertBottom(
              groups,
              virtualRects,
              draggedCardKey,
              draggedCardIdx,
              draggedCardColumnIdx,
              draggedGroupIdx,
              card.key,
              cardIdx,
              1,
              groupIdx
            );
          };
          dragAreas.push(topLeftDragArea, topRightDragArea, bottomLeftDragArea, bottomRightDragArea);
        });
      }
    });

    return dragAreas;
  }

  private generateTopLeftDragAreaForSingleColumnGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];
    const rowGapSize = this.#state.rowGapSize;

    const thereIsATopRowGap = cardIdx > 0 || groupIdx > 0;
    const topLeftDragAreaLeft = cardRect.left;
    const topLeftDragAreaTop = cardIdx > 0 || groupIdx > 0 ? cardRect.top - rowGapSize / 2 : cardRect.top;
    const topLeftDragAreaWidth = cardRect.width / 2;
    const topLeftDragAreaHeight = cardRect.height / 2 + (thereIsATopRowGap ? rowGapSize / 2 : 0);

    return {
      top: topLeftDragAreaTop,
      left: topLeftDragAreaLeft,
      width: topLeftDragAreaWidth,
      height: topLeftDragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateTopRightDragAreaForSingleColumnGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];
    const rowGapSize = this.#state.rowGapSize;

    const thereIsATopRowGap = cardIdx > 0 || groupIdx > 0;
    const topRightDragAreaLeft = cardRect.left + cardRect.width / 2;
    const topRightDragAreaTop = cardIdx > 0 || groupIdx > 0 ? cardRect.top - rowGapSize / 2 : cardRect.top;
    const topRightDragAreaWidth = cardRect.width / 2;
    const topRightDragAreaHeight = cardRect.height / 2 + (thereIsATopRowGap ? rowGapSize / 2 : 0);

    return {
      top: topRightDragAreaTop,
      left: topRightDragAreaLeft,
      width: topRightDragAreaWidth,
      height: topRightDragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateBottomLeftDragAreaForSingleColumnGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];
    const rowGapSize = this.#state.rowGapSize;
    const group = groups[groupIdx] as MovableCardsContainerSingleColumnGroup<T>;

    const thereIsABottomRowGap = cardIdx < group.cards.length - 1 || groupIdx < groups.length - 1;
    const bottomLeftDragAreaLeft = cardRect.left;
    const bottomLeftDragAreaTop = cardRect.top + cardRect.height / 2;
    const bottomLeftDragAreaWidth = cardRect.width / 2;
    const bottomLeftDragAreaHeight = cardRect.height / 2 + (thereIsABottomRowGap ? rowGapSize / 2 : 0);

    return {
      top: bottomLeftDragAreaTop,
      left: bottomLeftDragAreaLeft,
      width: bottomLeftDragAreaWidth,
      height: bottomLeftDragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateBottomRightDragAreaForSingleColumnGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];
    const group = groups[groupIdx] as MovableCardsContainerSingleColumnGroup;
    const thereIsABottomRowGap = cardIdx < group.cards.length - 1 || groupIdx < groups.length - 1;
    const rowGapSize = this.#state.rowGapSize;

    const bottomRightDragAreaLeft = cardRect.left + cardRect.width / 2;
    const bottomRightDragAreaTop = cardRect.top + cardRect.height / 2;
    const bottomRightDragAreaWidth = cardRect.width / 2;
    const bottomRightDragAreaHeight = cardRect.height / 2 + (thereIsABottomRowGap ? rowGapSize / 2 : 0);

    return {
      top: bottomRightDragAreaTop,
      left: bottomRightDragAreaLeft,
      width: bottomRightDragAreaWidth,
      height: bottomRightDragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateTopDragAreaForTwoColumnsGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];

    const rowGapSize = this.#state.rowGapSize;
    const thereIsATopRowGap = cardIdx > 0 || groupIdx > 0;
    const topDragAreaLeft = cardRect.left;
    const topDragAreaTop = cardIdx > 0 || groupIdx > 0 ? cardRect.top - rowGapSize / 2 : cardRect.top;
    const dragAreaWidth = cardRect.width;
    const dragAreaHeight = cardRect.height / 2 + (thereIsATopRowGap ? rowGapSize / 2 : 0);

    return {
      top: topDragAreaTop,
      left: topDragAreaLeft,
      width: dragAreaWidth,
      height: dragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateBottomDragAreaForTwoColumnsGroupCard(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    cardKey: string,
    cardIdx: number,
    groupIdx: number
  ): DragArea<T> {
    const cardRect = virtualRects[cardKey];
    const group = groups[groupIdx] as MovableCardsContainerTwoColumnsGroup<T>;

    const rowGapSize = this.#state.rowGapSize;
    const thereIsABottomRowGap = cardIdx < group.leftCards.length - 1 || groupIdx < groups.length - 1;
    const bottomDragAreaLeft = cardRect.left;
    const bottomDragAreaTop = cardRect.top + cardRect.height / 2;
    const bottomDragAreaWidth = cardRect.width;
    const bottomDragAreaHeight = cardRect.height / 2 + (thereIsABottomRowGap ? rowGapSize / 2 : 0);

    return {
      top: bottomDragAreaTop,
      left: bottomDragAreaLeft,
      width: bottomDragAreaWidth,
      height: bottomDragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateExtraSpaceDragAreaForTwoColumnsGroupLeftColumn(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    groupIdx: number
  ): DragArea<T> {
    const cardContainerRect = this.#state.cardContainerRect();
    const rowGapSize = this.#state.rowGapSize;

    const group = groups[groupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const firstRightCardRect = this.getFirstRightCardRect(group, virtualRects);
    const lastRightCardRect = this.getLastRightCardRect(group, virtualRects);
    const lastLeftCardRect = this.getLastLeftCardRect(group, virtualRects);

    const thereIsATopRowGap = groupIdx > 0;
    const thereIsABottomRowGap = groupIdx < groups.length - 1;

    const topDragAreaLeft = cardContainerRect.left;
    let topDragAreaTop = lastLeftCardRect ? lastLeftCardRect.top + lastLeftCardRect.height : firstRightCardRect.top;
    if (thereIsATopRowGap && !lastLeftCardRect) {
      topDragAreaTop -= rowGapSize / 2;
    }

    const dragAreaWidth = firstRightCardRect.width;
    let dragAreaHeight = lastLeftCardRect
      ? lastRightCardRect!.top + lastRightCardRect!.height - (lastLeftCardRect.top + lastLeftCardRect.height)
      : lastRightCardRect!.top + lastRightCardRect!.height - firstRightCardRect.top;
    if (thereIsATopRowGap && !lastLeftCardRect) {
      dragAreaHeight += rowGapSize / 2;
    }

    if (thereIsABottomRowGap) {
      if (lastLeftCardRect) {
        topDragAreaTop += rowGapSize / 2;
      } else {
        dragAreaHeight += rowGapSize / 2;
      }
    }

    return {
      top: topDragAreaTop,
      left: topDragAreaLeft,
      width: dragAreaWidth,
      height: dragAreaHeight,
      swapFn: () => [],
    };
  }

  private generateExtraSpaceDragAreaForTwoColumnsGroupRightColumn(
    virtualRects: Record<string, SimpleDomRect>,
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    groupIdx: number
  ): DragArea<T> {
    const columnGapSize = this.#state.columnGapSize;
    const rowGapSize = this.#state.rowGapSize;
    const group = groups[groupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const firstLeftCardRect = this.getFirstLeftCardRect(group, virtualRects);
    const lastLeftCardRect = this.getLastLeftCardRect(group, virtualRects);
    const lastRightCardRect = this.getLastRightCardRect(group, virtualRects);

    const thereIsATopRowGap = groupIdx > 0;
    const thereIsABottomRowGap = groupIdx < groups.length - 1;

    const topDragAreaLeft = firstLeftCardRect.left + firstLeftCardRect.width + columnGapSize;
    let topDragAreaTop = lastRightCardRect ? lastRightCardRect.top + lastRightCardRect.height : firstLeftCardRect.top;
    if (thereIsATopRowGap && !lastRightCardRect) {
      topDragAreaTop -= rowGapSize / 2;
    }

    const dragAreaWidth = firstLeftCardRect.width;
    let dragAreaHeight = lastRightCardRect
      ? lastLeftCardRect!.top + lastLeftCardRect!.height - (lastRightCardRect.top + lastRightCardRect.height)
      : lastLeftCardRect!.top + lastLeftCardRect!.height - firstLeftCardRect.top;
    if (thereIsATopRowGap && !lastRightCardRect) {
      dragAreaHeight += rowGapSize / 2;
    }

    if (thereIsABottomRowGap) {
      if (lastRightCardRect) {
        topDragAreaTop += rowGapSize / 2;
      } else {
        dragAreaHeight += rowGapSize / 2;
      }
    }

    return {
      top: topDragAreaTop,
      left: topDragAreaLeft,
      width: dragAreaWidth,
      height: dragAreaHeight,
      swapFn: () => [],
    };
  }

  private getFirstLeftCardRect(group: MovableCardsContainerTwoColumnsGroup<T>, virtualRects: Record<string, SimpleDomRect>): SimpleDomRect {
    return virtualRects[group.leftCards[0].key];
  }

  private getLastLeftCardRect(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    virtualRects: Record<string, SimpleDomRect>
  ): SimpleDomRect | null {
    return group.leftCards.length > 0 ? virtualRects[group.leftCards[group.leftCards.length - 1].key] : null;
  }

  private getLeftColumnHeight(group: MovableCardsContainerTwoColumnsGroup<T>, virtualRects: Record<string, SimpleDomRect>): number {
    const lastLeftCardRect = this.getLastLeftCardRect(group, virtualRects);
    return lastLeftCardRect ? lastLeftCardRect.top + lastLeftCardRect.height : 0;
  }

  private getFirstRightCardRect(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    virtualRects: Record<string, SimpleDomRect>
  ): SimpleDomRect {
    return virtualRects[group.rightCards[0].key];
  }

  private getLastRightCardRect(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    virtualRects: Record<string, SimpleDomRect>
  ): SimpleDomRect | null {
    return group.rightCards.length > 0 ? virtualRects[group.rightCards[group.rightCards.length - 1].key] : null;
  }

  private getRightColumnHeight(group: MovableCardsContainerTwoColumnsGroup<T>, virtualRects: Record<string, SimpleDomRect>): number {
    const lastRightCardRect = this.getLastRightCardRect(group, virtualRects);
    return lastRightCardRect ? lastRightCardRect.top + lastRightCardRect.height : 0;
  }
}
